import React, { useMemo } from "react";
import {
  BulkCancelSignalsJobArgs,
  JobToastType,
  selectJobsData,
} from "@shared/slices/jobs.slice";
import { useAppSelector } from "app/hooks";
import { useLocalJobData } from "utils/hooks/useLocalJobData";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BulkCancelProgressData, LanguageNameSpaces } from "@shared/types";
import BulkProgressJobToast from "common/BulkProgressJobToast";

interface BulkSignalsProgressToastProps {
  notificationApiRef: React.RefObject<any>;
}

const BulkSignalsProgressToast: React.FC<BulkSignalsProgressToastProps> = ({
  notificationApiRef,
}) => {
  const { localJobs, removeJob, isJobActiveInRedux } = useLocalJobData(
    JobToastType.Signal
  );
  return (
    <>
      {localJobs.map(({ args, id }) => (
        <SignalsJobToast
          key={id}
          notificationApiRef={notificationApiRef}
          args={args}
          toastKey={`bulk-cancel-signals-progress-toast-${id}`}
          onRemove={() => removeJob(id)}
          isJobActiveInRedux={() => isJobActiveInRedux(id)}
        />
      ))}
    </>
  );
};

export default React.memo(BulkSignalsProgressToast);

interface SignalsJobToastProps {
  notificationApiRef: React.RefObject<any>;
  args: BulkCancelSignalsJobArgs;
  toastKey: string;
  onRemove: () => void;
  isJobActiveInRedux: () => boolean;
}

const SignalsJobToast: React.FC<SignalsJobToastProps> = ({
  notificationApiRef,
  args,
  toastKey,
  onRemove,
  isJobActiveInRedux,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const selectJobData = useMemo(
    () => selectJobsData("bulkCancelSignalsJob", args),
    [args]
  );
  const jobResponse = useAppSelector(selectJobData);
  const rawData = jobResponse?.data?.data ?? jobResponse?.data ?? null;
  const jobData: BulkCancelProgressData | null = rawData?.signals_data ?? null;
  const isError = jobResponse?.isError ?? false;
  const errorMessage = jobResponse?.errorMessage;
  const total = args.signal_ids?.length ?? 0;
  const success = jobData?.done ?? 0;
  const failed = jobData?.failed ?? 0;
  const inactive = jobData?.inactive_market ?? 0;
  const completed = success + failed + inactive;
  const titleComplete = t("bulk_cancel_progress.closed_signals");
  const titleInProgress = t("bulk_cancel_progress.closing_signals", { total });
  const subtitle = t("bulk_cancel_progress.processed", { completed, total });

  return (
    <BulkProgressJobToast
      notificationApiRef={notificationApiRef}
      toastKey={toastKey}
      total={total}
      jobData={jobData}
      isError={isError}
      titleComplete={titleComplete}
      titleInProgress={titleInProgress}
      titleError={t("bulk_cancel_progress.error")}
      errorMessage={errorMessage}
      subtitle={subtitle}
      successLabel={t("bulk_cancel_progress.success")}
      failedLabel={t("bulk_cancel_progress.failed")}
      inactiveLabel={t("bulk_cancel_progress.inactive_symbols")}
      alreadyClosedLabel={t("bulk_cancel_progress.already_closed")}
      onRemove={onRemove}
      isJobActiveInRedux={isJobActiveInRedux}
    />
  );
};
